import { useCallback, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { Box, IconButton } from '@mui/material'
import FormatBoldIcon from '@mui/icons-material/FormatBold'
import FormatItalicIcon from '@mui/icons-material/FormatItalic'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'

export const docToPlainText = (doc) => {
  const lines = []
  doc.forEach((node) => {
    if (node.type.name === 'bulletList') {
      node.forEach((listItem) => {
        let text = ''
        listItem.forEach((child) => {
          if (child.type.name === 'paragraph') {
            text += inlineToPlainText(child)
          }
        })
        lines.push(`• ${text}`)
      })
    } else if (node.type.name === 'paragraph') {
      lines.push(inlineToPlainText(node))
    }
  })
  return lines.join('\n')
}

export const inlineToPlainText = (node) => {
  let result = ''
  node.forEach((child) => {
    if (child.isText) {
      const isBold = child.marks.some((m) => m.type.name === 'bold')
      const isItalic = child.marks.some((m) => m.type.name === 'italic')
      let text = child.text
      if (isBold) text = `**${text}**`
      if (isItalic) text = `*${text}*`
      result += text
    }
  })
  return result
}

export const escapeAndFormat = (text) => {
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
  return escaped
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
}

export const plainTextToHtml = (text) => {
  if (!text) return ''
  const lines = text.split('\n')
  let html = ''
  let inList = false

  for (const line of lines) {
    const bulletMatch = line.match(/^[•]\s?(.*)$/)
    if (bulletMatch) {
      if (!inList) {
        html += '<ul>'
        inList = true
      }
      html += `<li>${escapeAndFormat(bulletMatch[1])}</li>`
    } else {
      if (inList) {
        html += '</ul>'
        inList = false
      }
      html += `<p>${escapeAndFormat(line)}</p>`
    }
  }

  if (inList) html += '</ul>'
  return html
}

const ToolbarButton = ({ onClick, active, children, title }) => (
  <IconButton
    size="small"
    onClick={onClick}
    title={title}
    sx={{
      borderRadius: 1,
      p: 0.5,
      color: active ? 'primary.main' : 'text.secondary',
      bgcolor: active ? 'action.selected' : 'transparent',
      '&:hover': { bgcolor: 'action.hover' },
    }}
  >
    {children}
  </IconButton>
)

const PlainTextEditor = ({ value, onChange, placeholder, style }) => {
  const internalUpdate = useRef(false)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        strike: false,
        code: false,
        codeBlock: false,
        blockquote: false,
        heading: false,
        horizontalRule: false,
        orderedList: false,
      }),
    ],
    content: plainTextToHtml(value || ''),
    editorProps: {
      attributes: {
        'data-placeholder': placeholder || '',
      },
    },
    onUpdate: ({ editor: ed }) => {
      if (!onChange) return
      internalUpdate.current = true
      const plain = docToPlainText(ed.state.doc)
      onChange(plain)
      requestAnimationFrame(() => {
        internalUpdate.current = false
      })
    },
  })

  useEffect(() => {
    if (!editor || internalUpdate.current) return
    const currentPlain = docToPlainText(editor.state.doc)
    if (currentPlain !== (value || '')) {
      editor.commands.setContent(plainTextToHtml(value || ''), false)
    }
  }, [value, editor])

  const handleBold = useCallback(() => {
    editor?.chain().focus().toggleBold().run()
  }, [editor])

  const handleItalic = useCallback(() => {
    editor?.chain().focus().toggleItalic().run()
  }, [editor])

  const handleBulletList = useCallback(() => {
    editor?.chain().focus().toggleBulletList().run()
  }, [editor])

  const { minHeight, ...restStyle } = style || {}

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        ...restStyle,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 0.25,
          p: 0.5,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'grey.50',
        }}
      >
        <ToolbarButton
          onClick={handleBold}
          active={editor?.isActive('bold')}
          title="Bold"
        >
          <FormatBoldIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          onClick={handleItalic}
          active={editor?.isActive('italic')}
          title="Italic"
        >
          <FormatItalicIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          onClick={handleBulletList}
          active={editor?.isActive('bulletList')}
          title="Bullet list"
        >
          <FormatListBulletedIcon fontSize="small" />
        </ToolbarButton>
      </Box>
      <Box
        sx={{
          '& .ProseMirror': {
            minHeight: minHeight || 100,
            p: 1.5,
            outline: 'none',
            '& p': { m: 0, mb: '0.25em' },
            '& ul': { m: 0, pl: 3 },
            '& li': { mb: '0.25em' },
            '&.is-empty::before': {
              content: 'attr(data-placeholder)',
              color: 'text.disabled',
              float: 'left',
              pointerEvents: 'none',
              height: 0,
            },
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  )
}

PlainTextEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  style: PropTypes.object,
}

export default PlainTextEditor
